import React, { useState, useEffect, useCallback } from "react";
import { ClipLoader } from "react-spinners";
import moment from "moment";
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Button,
} from "reactstrap";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from "recharts";

import api from "../../../../../services/api";
import { percentFormat } from "../../../../../utils/percentFormat";
import { useTheme } from "../../../../../hooks/theme";
import { useCompanies } from "../../../../../hooks/companies"; // Importa o hook useCompanies
import { useLegend } from "../../../../../hooks/legend"; // Importe o useLegend
import ModalFilters from "../../../../../components/CustomModalFilters";

export default function ChartFretePorTranspNF({
  deferLoad = false,
  onRegisterFetch,
}) {
  const { darkMode } = useTheme();
  const { legendMode } = useLegend(); // Obtenha o legendMode
  const { empresa, transportadora, arrayParams } = useCompanies(); // Obtém as seleções de empresa e transportadora

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const color = darkMode ? "#e9ecef" : "#1e1e2f";
  const [modalFilters, setModalFilters] = useState(false);
  const [dataInicial, setDataInicial] = useState(
    moment().subtract(90, "days").startOf("month")
  );
  const [dataFinal, setDataFinal] = useState(moment().endOf("month"));
  const [auxDtInicial, setAuxDtInicial] = useState(
    moment().subtract(90, "days").startOf("month")
  );
  const [auxDtFinal, setAuxDtFinal] = useState(moment().endOf("month"));

  async function loadCharts() {
    try {
      setLoading(true);

      const params = {
        dataInicial: auxDtInicial.format("YYYY-MM-DD 00:00:00"),
        dataFinal: auxDtFinal.format("YYYY-MM-DD 23:59:59"),
      };

        if (arrayParams.embarcadores) {
          params.embarcadores = arrayParams.embarcadores;
        }

        if (arrayParams.transportadoras) {
          params.transportadoras = arrayParams.transportadoras;
        }

      const response = await api.get("/dashboard/custo/frete-por-nf-transp", {
        params,
      });

      console.log(response);
      if (response.status === 200) {
        console.log(response.data);
        setData(response.data);
      }
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (onRegisterFetch) onRegisterFetch(loadCharts);
    if (!deferLoad) {
      if (empresa) loadCharts(); // Carrega os gráficos quando empresa ou transportadora mudar
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auxDtFinal, auxDtInicial, empresa, transportadora]); // Adiciona empresa e transportadora como dependências

  const CustomTooltip = (props) => {
    const { active } = props;

    if (active) {
      const { payload } = props;
      const payFormatted = payload.map((pay) => ({
        ref: "Transportadora",
        descricao: pay.payload.descricao,
        name: "Valor:",
        value: percentFormat(pay.payload.value),
      }));
      return (
        <div
          className="tooltip-inner"
          style={{
            background: darkMode ? "#e9ecef" : "#1e1e2f",
            maxWidth: "500px",
            textAlign: "left",
          }}
        >
          <p style={{ color: darkMode ? "#000" : "#fff", fontWeight: 100 }}>
            <strong>{`${payFormatted[0].ref}: `}</strong>
            {payFormatted[0].descricao}
          </p>
          <p style={{ color: darkMode ? "#000" : "#fff", fontWeight: 100 }}>
            <strong>{`${payFormatted[0].name}: `}</strong>
            {payFormatted[0].value}
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLabel = (props) => {
    const { x, y, width, value } = props;
    return (
      <text
        x={x + width / 2}
        y={y - 10}
        fill={darkMode ? "#FFFFFF" : "#333333"}
        textAnchor="middle"
      >
        {percentFormat(value)}
      </text>
    );
  };

  const handleDateInicial = (e) => {
    setDataInicial(e);
  };

  const handleDateFinal = (e) => {
    setDataFinal(e);
  };

  const toggleModalFilters = () => {
    setModalFilters(!modalFilters);
  };

  const onSubmit = useCallback(() => {
    setAuxDtInicial(dataInicial);
    setAuxDtFinal(dataFinal);
    setModalFilters(!modalFilters);
  }, [dataFinal, dataInicial, modalFilters]);

  return (
    <Card
      style={{ boxShadow: "0px 1px 20px 0px rgb(0, 0, 0)" }}
      className="card-chart"
    >
      <ModalFilters
        open={modalFilters}
        toggle={toggleModalFilters}
        handleDateInicial={handleDateInicial}
        dataInicial={dataInicial}
        handleDateFinal={handleDateFinal}
        dataFinal={dataFinal}
        onSubmit={() => onSubmit()}
      />
      <CardHeader>
        <Row style={{ alignItems: "center" }}>
          <Col>
            <h4>Frete sobre NF por Transportadora</h4>
          </Col>
          <Col style={{ textAlign: "right" }}>
            <Button
              className="btn-sm dashboard-period-button"
              color="info"
              onClick={toggleModalFilters}
            >
              Período
            </Button>
          </Col>
        </Row>
        <CardTitle>
          Percentual de frete sobre o valor da NF dos últimos 3 meses
        </CardTitle>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div style={{ textAlign: "center" }}>
            <ClipLoader
              sizeUnit="px"
              size={90}
              color="#1d8cf8"
              loading={loading}
            />
            <br />
            <br />
            <h1 className="text-info" style={{ textAlign: "center" }}>
              Carregando ...
            </h1>
          </div>
        ) : (
          <Row className="justify-content-center">
            <Col>
              <ResponsiveContainer width="100%" height={250}>
                {data.length > 0 ? (
                  <BarChart
                    data={data}
                    margin={{ top: 20, right: 20, left: 20, bottom: 10 }}
                  >
                    <defs>
                      <linearGradient
                        id="barFrete2"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="20%"
                          stopColor="#ffd600"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="50%"
                          stopColor="#ffd600"
                          stopOpacity={0.2}
                        />
                        <stop
                          offset="90%"
                          stopColor="#ffd600"
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      stroke="rgba(45,206,137, 0.1)"
                      strokeWidth={1}
                    />
                    <XAxis
                      dataKey="descricao"
                      stroke={color}
                      style={{ fontWeight: 100 }}
                    />
                    <YAxis
                      tickFormatter={(tick) => percentFormat(tick)}
                      stroke={color}
                      style={{ fontWeight: 100 }}
                      width={60}
                    />
                    <Tooltip content={(props) => CustomTooltip(props)} />
                    <Legend />
                    <Bar
                      name="(%) Frete por Valor de Nota Fiscal"
                      dataKey="value"
                      type="monotone"
                      stroke="#ffd600"
                      fillOpacity={1}
                      fill="url(#barFrete2)"
                    >
                      {legendMode && (
                        <LabelList dataKey="value" content={CustomLabel} />
                      )}
                    </Bar>
                  </BarChart>
                ) : (
                  <div style={{ textAlign: "center" }}>
                    <h5>Nenhum dado para apresentar!</h5>
                  </div>
                )}
              </ResponsiveContainer>
            </Col>
          </Row>
        )}
      </CardBody>
    </Card>
  );
}
